import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState, useCallback, useRef } from "react";
import Autosuggest from "react-autosuggest";
import Button from "../Button";
import ClearIcon from "@material-ui/icons/Clear";
import Fade from "@material-ui/core/Fade";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import PropTypes from "prop-types";
import SearchIcon from "@material-ui/icons/Search";
import TextInput from "../TextInput";
import classNames from "classnames";
import { useStyles } from "./styles";
import { debounce } from "lodash";
import { useSnackbar } from "notistack";
import SearchInputFilters from "./SearchInputFilter";
export const SearchInput = props => {
  const {
    useHotkey,
    className,
    style,
    onSearch,
    onSearchSuggestion,
    initialWidth,
    maxWidth,
    onChange,
    suggestions,
    maxSuggestions,
    renderSuggestion,
    mustSelectSuggestion,
    onClear,
    searchOnChange,
    placeholder,
    AutosuggestProps,
    ref,
    variant,
    value,
    filters,
    activeFilters,
    onFilterReset,
    ...others
  } = props;
  const classes = useStyles(props);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();
  const [renderedSuggestions, setRenderedSuggestions] = useState([]);
  // Only used when not controlled, otherwise use onChange and value
  const [uncontrolledValue, setUncontrolledValue] = useState("");

  // useEffect(() => {
  // 	if (others.value !== undefined && value !== others.value) setUncontrolledValue(others.value);
  // }, [others.value, value]);

  const {
    enqueueSnackbar
  } = useSnackbar();
  const sortSuggestions = (a, b) => a.index < b.index ? -1 : a.index > b.index ? 1 : a.value.text < b.value.text ? -1 : a.value.text > b.value.text ? 1 : 0;
  const getSuggestions = useCallback(value => {
    if (typeof onSearchSuggestion === "function") return false;
    const testValue = value.trim().toLowerCase();
    if (testValue.length > 0) {
      const weightedSuggestions = [];
      suggestions?.forEach(suggestion => {
        const index = suggestion.text.toLowerCase().indexOf(testValue);
        if (index !== -1) weightedSuggestions.push({
          value: suggestion,
          index
        });
      });
      weightedSuggestions.sort(sortSuggestions);
      setRenderedSuggestions(weightedSuggestions.slice(0, maxSuggestions).map(weightedSuggestion => weightedSuggestion.value));
    } else {
      setRenderedSuggestions([]);
    }
  }, [maxSuggestions, suggestions, onSearchSuggestion]);
  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);
  const handleClear = () => {
    setUncontrolledValue("");
    if (typeof onChange === "function") {
      onChange({
        target: {
          value: ""
        }
      });
    }
    if (typeof onClear === "function") onClear();
  };
  const handleSearch = () => {
    if (typeof onSearch === "function") {
      inputRef.current?.blur();
      setTimeout(() => handleBlur(), 100); // Small delay added to ensure animation fired
      onSearch(value !== undefined ? value : uncontrolledValue);
    }
  };
  useEffect(() => {
    let listener;
    if (inputRef.current && useHotkey) {
      listener = event => {
        if ((event.ctrlKey || event.metaKey) && event.key === "k") {
          event.preventDefault();
          inputRef.current?.focus();
          inputRef.current?.select();
        }
      };
      document.addEventListener("keydown", listener);
    }
    return () => {
      if (inputRef && useHotkey) {
        document.removeEventListener("keydown", listener);
      }
    };
  }, [inputRef, useHotkey]);
  const handleKeyDown = event => {
    // Handle enter press on input
    if (event.keyCode === 13) {
      handleSearch();
    } else if (event.key === "Escape") {
      inputRef.current?.blur();
      handleBlur();
    }
  };
  const renderedTextInputProps = {
    ...others,
    className: classNames(className, classes.root, {
      [classes.focused]: focused,
      [classes.filterButtonPadding]: filters
    }),
    value: value !== undefined ? value : uncontrolledValue,
    variant,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    placeholder: placeholder,
    inputProps: {
      "aria-label": "label" in others ? undefined : placeholder,
      ref: inputRef,
      ...others.inputProps
    },
    endAdornment: (onChange || mustSelectSuggestion) && searchOnChange ? value === "" || value === undefined && uncontrolledValue === "" ? /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: filters
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      "aria-label": "Search",
      disabled: true
    }, /*#__PURE__*/React.createElement(SearchIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, props)) : /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: filters
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      title: "Clear search",
      onClick: handleClear
    }, /*#__PURE__*/React.createElement(ClearIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, props)) : value === "" || value === undefined && uncontrolledValue === "" ? /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: filters
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      "aria-label": "Search",
      disabled: true
    }, /*#__PURE__*/React.createElement(SearchIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, props)) : /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: filters
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      title: "Search",
      onClick: handleSearch
    }, /*#__PURE__*/React.createElement(SearchIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, props))
  };
  const renderInputComponent = props => {
    const {
      ref,
      ...others
    } = props;
    if (typeof ref === "function") ref(inputRef);
    return /*#__PURE__*/React.createElement(TextInput, _extends({}, renderedTextInputProps, others, {
      size: renderedTextInputProps.size
    }));
  };
  const renderSuggestionsContainer = ({
    containerProps,
    children
  }) => /*#__PURE__*/React.createElement(Fade, {
    in: true
  }, /*#__PURE__*/React.createElement(Paper, containerProps, children));
  renderSuggestionsContainer.propTypes = {
    containerProps: PropTypes.object,
    children: PropTypes.node
  };
  const onSuggestionsClearRequested = () => {
    setRenderedSuggestions([]);
  };
  const localRenderSuggestion = (suggestion, otherProps) => {
    if (typeof renderSuggestion === "function") {
      return renderSuggestion(suggestion, otherProps);
    } else {
      const {
        query
      } = otherProps;
      const queryIndexStart = suggestion.text.toLowerCase().indexOf(query.toLowerCase());
      const queryIndexEnd = queryIndexStart + query.length;
      const before = suggestion.text.slice(0, queryIndexStart);
      const highlighted = suggestion.text.slice(queryIndexStart, queryIndexEnd);
      const after = suggestion.text.slice(queryIndexEnd, suggestion.text.length);
      return /*#__PURE__*/React.createElement(React.Fragment, null, before, /*#__PURE__*/React.createElement("strong", null, highlighted), after);
    }
  };
  const getSuggestionValue = suggestion => suggestion.text;
  const theme = {
    container: classes.container,
    suggestionsContainer: classes.suggestionsContainer,
    suggestionsList: classes.suggestionsList,
    suggestion: classes.suggestion,
    suggestionHighlighted: classes.suggestionHighlighted
  };
  const handleSearchSuggestionRequest = async ({
    value
  }) => {
    if (value.length > 0 && onSearchSuggestion) {
      try {
        const items = await onSearchSuggestion(value);
        setRenderedSuggestions(items);
      } catch (e) {
        enqueueSnackbar(`We couldn't fetch the suggestion list. It may be due to some network instability. Try again later.`, {
          variant: "error"
        });
      }
    }
  };

  // Use the dependencies from the function inside of debounce
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearchSuggestions = useCallback(debounce(handleSearchSuggestionRequest, 300), [onSearchSuggestion, enqueueSnackbar]);
  if (process.env.NODE_ENV === "development") {
    if (typeof onSearchSuggestion === "function" && suggestions) {
      // eslint-disable-next-line no-console
      console.warn("suggestions prop will be ignored and use instead the list coming from onSearchSuggestion prop.");
    }
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classes.outerWrapper,
    style: style
  }, suggestions ? /*#__PURE__*/React.createElement(Autosuggest, _extends({
    suggestions: renderedSuggestions,
    onSuggestionsFetchRequested: debounceSearchSuggestions,
    onSuggestionsClearRequested: onSuggestionsClearRequested,
    getSuggestionValue: getSuggestionValue,
    renderSuggestion: localRenderSuggestion,
    inputProps: {
      ...renderedTextInputProps,
      onChange: (e, suggestStuff) => {
        const {
          newValue,
          method
        } = suggestStuff;
        setUncontrolledValue(newValue);
        if (typeof onChange === "function") onChange({
          target: {
            value: newValue
          }
        });
        if (method === "type") {
          getSuggestions(newValue);
        } else if (typeof onSearch === "function") {
          if (method === "click") {
            onSearch(newValue);
          } else if (method === "enter") {
            onSearch(newValue);
          }
        }
      },
      size: undefined
    },
    renderInputComponent: renderInputComponent,
    theme: theme,
    renderSuggestionsContainer: renderSuggestionsContainer,
    focusInputOnSuggestionClick: false,
    highlightFirstSuggestion: mustSelectSuggestion,
    "aria-label": placeholder
  }, AutosuggestProps)) : /*#__PURE__*/React.createElement(TextInput, _extends({}, renderedTextInputProps, {
    onChange: e => {
      setUncontrolledValue(e.target.value);
      if (typeof onChange === "function") onChange(e);
    }
  })));
};
SearchInput.defaultProps = {
  placeholder: "Search...",
  initialWidth: 100,
  maxWidth: "100%",
  maxSuggestions: 6
};
export default SearchInput;